"use client";

import MobileProspectCard from "@/components/molecules/MobileProspectCard";
import ProgramProspectsCard from "@/components/molecules/ProgramProspectsCard";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  carouselClick,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { useCallback, useMemo, useRef, useState } from "react";

// API Metadata structure (from CMS)
interface APIProgramProspectMetadata {
  id: string;
  meta_title: string;
  meta_status: boolean;
  meta_priority: number;
  meta_description: string;
  meta_mobile_icon?: string;
  meta_desktop_icon?: string;
  meta_mobile_icon_alt?: string;
  meta_desktop_icon_alt?: string;
  meta_title_text_color?: string;
  meta_mobile_card_color?: string;
  meta_desktop_card_color?: string;
  meta_description_text_color?: string;
  meta_mobile_title_text_color?: string;
}

// Internal metadata structure used by components
interface ProgramProspectMetadata {
  id: string;
  icon: string;
  alt_icon?: string;
  text: string;
  priority: number;
  hover_icon?: string;
  description: string;
  title_text_color?: string;
  description_text_color?: string;
  mobile_icon?: string;
  mobile_card_color?: string;
  desktop_card_color?: string;
  mobile_title_text_color?: string;
  unselected_text_background_color?: string;
}

interface ProgramProspectsData {
  id?: number;
  page_id?: number;
  component_id?: number;
  title: string;
  title_text_color?: string;
  sub_title?: string;
  sub_title_text_color?: string;
  cta_text?: string;
  cta_text_color?: string;
  cta_background_color?: string;
  cta_icon?: string;
  cta_icon_alt?: string;
  background_color?: string;
  variant?: string;
  status?: number;
  metadata: APIProgramProspectMetadata[];
}

interface ProgramProspects1Props {
  data: {
    data: ProgramProspectsData;
  };
}

function ProgramProspects1({ data }: ProgramProspects1Props) {
  const {
    title = "",
    title_text_color = "#000000",
    sub_title = "",
    sub_title_text_color = "#000000",
    background_color = "#F8F9FA",
    metadata = [],
    status = 1,
  } = data?.data || {};

  console.log("data", data);
  const isMobile = useIsMobile(800);
  const ref = useRef<HTMLElement>(null);
  const swipeRef = useRef<HTMLDivElement>(null);
  const previousIndexRef = useRef<number>(-1);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Touch handling
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "program_prospects_section");
  //   }
  // }, []);

  // Transform API metadata to internal format and sort by priority
  const sortedMetadata: ProgramProspectMetadata[] = useMemo(() => {
    return metadata
      .filter((item) => item.meta_status !== false)
      .map((item) => ({
        id: item.id,
        icon: item.meta_desktop_icon || "",
        alt_icon: item.meta_desktop_icon_alt,
        text: item.meta_title,
        priority: item.meta_priority || 0,
        description: item.meta_description || "",
        title_text_color: item.meta_title_text_color,
        description_text_color: item.meta_description_text_color,
        mobile_icon: item.meta_mobile_icon,
        mobile_card_color: item.meta_mobile_card_color,
        desktop_card_color: item.meta_desktop_card_color,
        mobile_title_text_color: item.meta_mobile_title_text_color,
      }))
      .sort((a, b) => (a.priority || 0) - (b.priority || 0));
  }, [metadata]);

  // Handle swipe - only allow right swipe (to go forward)
  const handleSwipe = useCallback(() => {
    const swipeDistance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    // Left swipe (drag left = go to next)
    if (swipeDistance > minSwipeDistance && sortedMetadata.length > 0) {
      const newIndex = (currentIndex + 1) % sortedMetadata.length;

      // Track swipe
      if (
        previousIndexRef.current !== newIndex &&
        previousIndexRef.current !== -1
      ) {
        const currentItem = sortedMetadata[newIndex];
        carouselClick({
          section: "program_prospects_section",
          label:
            currentItem?.text ||
            currentItem?.description ||
            title ||
            `prospect_${newIndex + 1}`,
        });
      }

      previousIndexRef.current = newIndex;
      setCurrentIndex(newIndex);
    }
    // Right swipe (drag right = go back) - BLOCKED
  }, [currentIndex, sortedMetadata, title]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    handleSwipe();
    // Reset
    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  // Mouse drag support for desktop testing
  const handleMouseDown = (e: React.MouseEvent) => {
    touchStartX.current = e.clientX;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    touchEndX.current = e.clientX;
    handleSwipe();
    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  // Don't render if status is 0
  if (!status) {
    return null;
  }

  // Get 3 cards starting from current index (with circular loop)
  const getStackedCards = () => {
    if (sortedMetadata.length === 0) return [];
    const cards = [];
    const numCards = Math.min(3, sortedMetadata.length);
    for (let i = 0; i < numCards; i++) {
      const index = (currentIndex + i) % sortedMetadata.length;
      cards.push({ ...sortedMetadata[index], stackPosition: i });
    }
    return cards;
  };

  return (
    <section
      ref={ref}
      className={cn(
        // Section is full-bleed (background spans the viewport); the inner grid
        // keeps its own max-width + mx-auto so the cards stay centered/capped.
        "mx-auto flex flex-col items-center justify-center w-full",
        "px-5 py-8 sm:px-8 sm:py-11 gap-6 sm:gap-8",
      )}
      style={{ backgroundColor: background_color }}
    >
      {/* Title */}
      <div className="text-center w-full">
        <h2
          className={cn(
            "text-heading-6-semibold",
            "sm:text-heading-5-semibold",
            "lg:text-new-heading-34-semibold",
            "xl:text-new-heading-42-semibold",
            "mb-spacing-8 sm:mb-spacing-12",
            "line-clamp-1",
          )}
          style={{ color: title_text_color }}
        >
          {title}
        </h2>

        {/* Subtitle */}
        {sub_title && (
          <p
            className={cn(
              "text-body-3-regular",
              "md:text-body-2-regular",
              "xl:text-new-body-24-regular",
              "max-w-4xl mx-auto",
              "line-clamp-3 sm:line-clamp-2",
            )}
            style={{ color: sub_title_text_color }}
          >
            {sub_title}
          </p>
        )}
      </div>

      {isMobile ? (
        <div className="w-full flex justify-center">
          {/* Swipe container */}
          <div
            ref={swipeRef}
            className="relative h-[180px] w-full max-w-[380px] cursor-grab active:cursor-grabbing select-none"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
          >
            {/* Stacked cards - rendered once, animated based on currentIndex */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative h-[180px] w-[360px]">
                {getStackedCards().map((item, stackIndex) => {
                  const variant =
                    stackIndex === 0
                      ? "large"
                      : stackIndex === 1
                        ? "medium"
                        : "small";

                  const offsetX = stackIndex * 74;
                  // Offset Y: 0 for front, 20 for middle, 34 for back (decreasing steps)
                  const offsetY =
                    stackIndex === 0 ? 0 : stackIndex === 1 ? 20 : 34;
                  const zIndex = 30 - stackIndex * 10;
                  const opacity =
                    stackIndex === 0 ? 1 : stackIndex === 1 ? 0.8 : 0.2;

                  const mobileData = {
                    ...item,
                    icon: item.mobile_icon || item.icon,
                    title_text_color:
                      item.mobile_title_text_color || item.title_text_color,
                    description_text_color: item.description_text_color,
                    opacity: opacity,
                  };

                  return (
                    <div
                      key={`${item.id}-${stackIndex}-${currentIndex}`}
                      className="absolute transition-all duration-300 ease-out"
                      style={{
                        right: `${offsetX}px`,
                        top: `${offsetY}px`,
                        zIndex,
                      }}
                    >
                      <MobileProspectCard data={mobileData} variant={variant} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={cn(
            // Section is full-bleed (background spans the viewport); the grid keeps
            // its own max-width + mx-auto so the content stays within the bounds it
            // was previously wrapped in, centered.
            "grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6 w-full max-w-7xl mx-auto mb-spacing-14 sm:mb-spacing-20",
            "sm:max-w-[1008px] md:max-w-[1158px] lg:max-w-[1358px] xl:max-w-[1678px]",
          )}
        >
          {sortedMetadata.map((item: ProgramProspectMetadata) => (
            <ProgramProspectsCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </section>
  );
}

export default ProgramProspects1;
